import { MIN_PASSWORD_LENGTH } from '@sigora/domain';
import { type FormEvent, useRef, useState } from 'react';

import { signUp } from '../api/client.ts';
import { RouteLink } from '../studies/RouteLink.tsx';
import type { Route } from '../studies/routing.ts';
import { CheckEmailNotice } from './CheckEmailNotice.tsx';
import { failureMessage, refusedField } from './failure.ts';
import { displayNameProblem, passwordProblem } from './validation.ts';

export interface SignupViewProps {
  onNavigate: (route: Route) => void;
}

type Field = 'name' | 'email' | 'password';
type FieldErrors = Partial<Record<Field, string>>;

/**
 * How long the check-email screen holds "send it again" after a signup. It mirrors the server's
 * per-address mail cooldown: a repeated signup inside it replaces the account's verification link
 * and mails nothing, which would leave the user with no link that works.
 */
export const SIGNUP_RESEND_HOLD_MS = 60_000;

const isField = (value: string | null): value is Field =>
  value === 'name' || value === 'email' || value === 'password';

/**
 * Creating an account. It never signs anybody in: the account is usable once the address has been
 * confirmed from the mail this sends, and until then there is only the check-email screen, which
 * reads the same whether the address was new or already had an account.
 */
export const SignupView = ({ onNavigate }: SignupViewProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  /**
   * Set from the moment a signup is sent until it is refused, read synchronously so a second
   * submit in the same tick — before `pending` has re-rendered the button — sends nothing.
   */
  const inFlight = useRef(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  /**
   * The request the server took, kept so "send it again" can repeat it. Repeating the signup,
   * rather than asking for a verification resend, is what gets each address its own mail again: an
   * address that already has a confirmed account was sent the account-exists mail, and a resend for
   * it sends nothing. The answer is the same 202 either way, so repeating it tells nobody anything.
   */
  const [sent, setSent] = useState<{ email: string; password: string; name: string } | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (inFlight.current) {
      return;
    }
    setError(null);

    const found: FieldErrors = {};
    const nameProblem = displayNameProblem(name);
    const newPasswordProblem = passwordProblem(password);
    if (nameProblem !== null) {
      found.name = nameProblem;
    }
    if (newPasswordProblem !== null) {
      found.password = newPasswordProblem;
    }
    setFieldErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }

    inFlight.current = true;
    setPending(true);
    const request = { email, password, name: name.trim() };
    void signUp(request).then(
      // Left in flight: the form is replaced by the check-email screen and never sends again.
      () => setSent(request),
      (failure: unknown) => {
        inFlight.current = false;
        setPending(false);
        const field = refusedField(failure);
        if (isField(field)) {
          setFieldErrors({ [field]: failureMessage(failure) });
        } else {
          setError(failureMessage(failure));
        }
      },
    );
  };

  if (sent !== null) {
    return (
      <main className="login" data-test-id="signup-view">
        <CheckEmailNotice
          email={sent.email}
          sendAgain={() => signUp(sent)}
          onNavigate={onNavigate}
          holdAgainMs={SIGNUP_RESEND_HOLD_MS}
        />
      </main>
    );
  }

  const fieldError = (field: Field) =>
    fieldErrors[field] === undefined ? null : (
      <p
        className="login-error"
        id={`signup-${field}-error`}
        data-test-id={`signup-${field}-error`}
        role="alert"
      >
        {fieldErrors[field]}
      </p>
    );

  return (
    <main className="login" data-test-id="signup-view">
      <h1>Create your Sigora account</h1>
      <form onSubmit={submit}>
        <label htmlFor="signup-name">Your name</label>
        <input
          id="signup-name"
          data-test-id="signup-name"
          autoComplete="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-invalid={fieldErrors.name !== undefined}
          aria-describedby={fieldErrors.name === undefined ? undefined : 'signup-name-error'}
          required
        />
        {fieldError('name')}

        <label htmlFor="signup-email">E-mail</label>
        <input
          id="signup-email"
          data-test-id="signup-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          aria-invalid={fieldErrors.email !== undefined}
          aria-describedby={fieldErrors.email === undefined ? undefined : 'signup-email-error'}
          required
        />
        {fieldError('email')}

        <label htmlFor="signup-password">Password</label>
        <input
          id="signup-password"
          data-test-id="signup-password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          aria-invalid={fieldErrors.password !== undefined}
          aria-describedby={
            fieldErrors.password === undefined ? 'signup-password-hint' : 'signup-password-error'
          }
          required
        />
        <p className="login-note" id="signup-password-hint" data-test-id="signup-password-hint">
          At least {MIN_PASSWORD_LENGTH} characters.
        </p>
        {fieldError('password')}

        {error === null ? null : (
          <p className="login-error" data-test-id="signup-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" data-test-id="signup-submit" disabled={pending}>
          {pending ? 'Creating your account…' : 'Create account'}
        </button>
      </form>

      <p className="login-links">
        Already have an account?{' '}
        <RouteLink to={{ name: 'studies' }} onNavigate={onNavigate} testId="signup-sign-in">
          Sign in
        </RouteLink>
      </p>
    </main>
  );
};
